import { useState, useRef, useEffect } from "react";

const SLIDES = [
  {
    eyebrow: "Welcome to hypha",
    headline: ["A single thread.", "Then a forest."],
    body: "A hypha is one microscopic filament. Alone, invisible. Together, the most resilient living network on earth.\n\nThat's what you are \u2014 when you know what you bring.",
  },
  {
    eyebrow: "One question",
    headline: ["Every day,", "one honest answer."],
    body: "\u201CWhat did you contribute today that no AI could replicate?\u201D\n\nNot a task. Not a deliverable. A moment of being irreducibly, undeniably human.",
  },
  {
    eyebrow: "Let's begin",
    headline: ["What should", "we call you?"],
    body: null,
  },
];

const TIMES = [
  { label: "7am", sublabel: "Morning", value: "07:00" },
  { label: "9am", sublabel: "Morning", value: "09:00" },
  { label: "12pm", sublabel: "Midday", value: "12:00" },
  { label: "8pm", sublabel: "Evening", value: "20:00" },
];

export default function Onboarding({ onComplete }) {
  const [slide, setSlide] = useState(0);
  const [name, setName] = useState("");
  const [time, setTime] = useState("09:00");
  const nameRef = useRef(null);

  useEffect(() => {
    if (slide === 2) {
      setTimeout(() => nameRef.current?.focus(), 300);
    }
  }, [slide]);

  function next() {
    if (slide < 2) { setSlide((s) => s + 1); return; }
    onComplete({ name: name.trim() || "Friend", notifyTime: time });
  }

  const s = SLIDES[slide];

  return (
    <div
      data-testid="onboarding-screen"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "max(40px, calc(var(--safe-top) + 32px)) max(24px, var(--safe-right)) max(32px, calc(var(--safe-bot) + 24px)) max(24px, var(--safe-left))",
        animation: "fadeRise 0.45s ease",
      }}
    >
      {/* Slide content */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        justifyContent: "center", maxHeight: 560, margin: "auto 0",
      }}>
        <p style={{
          fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
          letterSpacing: "0.18em", color: "var(--teal)",
          textTransform: "uppercase", marginBottom: 24,
        }}>
          {s.eyebrow}
        </p>

        <h1 style={{
          fontSize: "clamp(28px, 9vw, 42px)",
          fontWeight: 400, lineHeight: 1.18,
          color: "var(--cream)", marginBottom: 18,
        }}>
          {s.headline[0]}<br />
          <em style={{ fontStyle: "italic", color: "var(--teal-mid)" }}>
            {s.headline[1]}
          </em>
        </h1>

        {s.body && (
          <p style={{
            fontSize: 17, lineHeight: 1.72,
            color: "var(--cream-dim)",
            whiteSpace: "pre-line", marginBottom: 4,
          }}>
            {s.body}
          </p>
        )}

        {/* Slide 3 form */}
        {slide === 2 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: "block",
                fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
                letterSpacing: "0.12em", color: "var(--teal)",
                textTransform: "uppercase", marginBottom: 10,
              }}>
                Your name
              </label>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") next(); }}
                placeholder="First name"
                autoComplete="given-name"
                data-testid="onboarding-name-input"
                style={{
                  width: "100%", fontSize: 16,
                  background: "var(--teal-glow)",
                  border: "1px solid var(--teal-border)",
                  borderRadius: 8, padding: "0 16px",
                  height: 52, color: "var(--cream)", outline: "none",
                  transition: "border-color 0.2s",
                }}
              />
            </div>

            <div>
              <label style={{
                display: "block",
                fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
                letterSpacing: "0.12em", color: "var(--teal)",
                textTransform: "uppercase", marginBottom: 10,
              }}>
                Best time to check in{" "}
                <span style={{ opacity: 0.5, fontSize: 10 }}>(UTC)</span>
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {TIMES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTime(t.value)}
                    data-testid={`time-btn-${t.value}`}
                    style={{
                      background: time === t.value ? "rgba(29,158,117,0.22)" : "var(--teal-glow)",
                      border: `1px solid ${time === t.value ? "var(--teal)" : "var(--teal-border)"}`,
                      borderRadius: 8, height: 52,
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: 2,
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <span style={{
                      fontFamily: "var(--mono)", fontSize: 14, fontWeight: 400,
                      color: time === t.value ? "var(--teal-lt)" : "var(--cream-dim)",
                      letterSpacing: "0.02em",
                    }}>
                      {t.label}
                    </span>
                    <span style={{
                      fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
                      color: time === t.value ? "rgba(159,225,203,0.6)" : "var(--cream-faint)",
                      letterSpacing: "0.06em",
                    }}>
                      {t.sublabel}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom: dots + CTA */}
      <div style={{ flexShrink: 0, paddingTop: 24 }}>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 }}>
          {SLIDES.map((_, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: "50%",
              background: i === slide ? "var(--teal)" : "var(--teal-border)",
              transform: i === slide ? "scale(1.35)" : "scale(1)",
              transition: "all 0.3s",
            }} />
          ))}
        </div>

        <button
          type="button"
          onClick={next}
          data-testid="onboarding-continue-btn"
          style={{
            width: "100%", height: 52,
            background: "var(--teal)", color: "var(--deep)",
            border: "none", borderRadius: 10,
            fontFamily: "var(--mono)", fontSize: 14, fontWeight: 400,
            letterSpacing: "0.06em", cursor: "pointer",
            transition: "background 0.2s, transform 0.1s",
          }}
        >
          {slide < 2 ? "Continue" : "Start my hypha"}
        </button>
      </div>
    </div>
  );
}
